import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { initializeFirestore, setLogLevel, persistentLocalCache, persistentMultipleTabManager, doc, getDocFromServer, setDoc as firestoreSetDoc, deleteDoc as firestoreDeleteDoc, getFirestore, writeBatch } from 'firebase/firestore';
import { safeClone, safeStringify } from '../lib/utils';
import { localDb } from './localDbService';

// @ts-ignore
import firebaseConfig from '../../firebase-applet-config.json';

// Initialize Firebase SDK
const { firestoreDatabaseId, ...firebaseOptions } = firebaseConfig as any;
const app = initializeApp(firebaseOptions);
export const auth = getAuth(app);

// Proactively set persistence
setPersistence(auth, browserLocalPersistence).catch(err => {
  console.warn('[AUTH] Persistence setup failed:', err);
});

// Use a more robust initialization with additional stability flags
// Using persistentLocalCache to minimize Cloud Firestore reads and prevent quota abuse
let firestoreInstance: any;
try {
  firestoreInstance = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    experimentalForceLongPolling: true,
    ignoreUndefinedProperties: true,
    // @ts-ignore - Some older versions of SDK might not have this in types but it works
    useFetchStreams: false,
  }, firestoreDatabaseId);
} catch (e) {
  console.warn('initializeFirestore with tab persistent cache failed (expected in sandbox/iframes), falling back to standard initialization:', e);
  try {
    firestoreInstance = getFirestore(app, firestoreDatabaseId);
  } catch (err) {
    console.error('getFirestore failed as well:', err);
  }
}

export const db = firestoreInstance;

export const getCandidateFirestoreDatabases = (): any[] => {
  return [db].filter(Boolean);
};

export let isQuotaExceeded = false;

if (typeof window !== 'undefined') {
  if ((window as any).__firestoreQuotaExceeded) {
    isQuotaExceeded = true;
  }
  window.addEventListener('firestore-quota-exceeded', () => {
    isQuotaExceeded = true;
  });
  window.addEventListener('firestore-quota-cleared', () => {
    isQuotaExceeded = false;
  });
}

export function setIsQuotaExceeded(val: boolean) {
  const prev = isQuotaExceeded;
  isQuotaExceeded = val;
  if (typeof window !== 'undefined') {
    (window as any).__firestoreQuotaExceeded = val;
    if (val && !prev) {
      window.dispatchEvent(new CustomEvent('firestore-quota-exceeded'));
    } else if (!val && prev) {
      window.dispatchEvent(new CustomEvent('firestore-quota-cleared'));
    }
  }
}

export function isFirestoreQuotaExhaustedError(err: unknown): boolean {
  if (!err) return false;
  const anyErr = err as any;
  if (anyErr?.name === 'QuotaExceededError' || anyErr?.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
    return false; // Browser localStorage / DOM quota, NOT Firestore
  }
  const code = anyErr?.code || '';
  if (code === 'resource-exhausted') return true;
  
  const msg = (err instanceof Error ? `${err.name} ${err.message}` : String(err)).toLowerCase();
  if (msg.includes('localstorage') || msg.includes('storage proxy') || msg.includes('quotaexceedederror')) {
    return false;
  }
  return msg.includes('resource-exhausted') || 
         msg.includes('resource_exhausted') || 
         (msg.includes('firestore') && (msg.includes('quota exceeded') || msg.includes('quota limit exceeded'))) ||
         msg.includes('quota limit exceeded') ||
         msg.includes('quota exceeded');
}

export async function checkFirestoreConnection(): Promise<boolean> {
  if (!db) return false;
  try {
    const testDocRef = doc(db, 'test', 'connection');
    await getDocFromServer(testDocRef);
    setIsQuotaExceeded(false);
    return true;
  } catch (err: any) {
    if (isFirestoreQuotaExhaustedError(err)) {
      setIsQuotaExceeded(true);
      return false;
    }
    // If it's a network offline/unavailable error, don't mark quota exceeded
    return false;
  }
}

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface OfflineOperation {
  id: string;
  type: 'set' | 'delete';
  path: string;
  data?: any;
  options?: any;
  timestamp: number;
}

const OFFLINE_QUEUE_KEY = 'main_nutrition_offline_queue';

function getOfflineQueue(): OfflineOperation[] {
  try {
    const q = localDb.getItemSync(OFFLINE_QUEUE_KEY);
    return q ? JSON.parse(q) : [];
  } catch (e) {
    return [];
  }
}

function saveOfflineQueue(queue: OfflineOperation[]) {
  try {
    localDb.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(safeClone(queue)));
  } catch (e) {
    console.warn('Failed to save offline queue to LocalDB', e);
  }
}

/**
 * Coalesces and deduplicates sequential offline operations targeting the same document path.
 * This collapses redundant intermediate sets and deletes into single atomic net operations.
 */
export function coalesceOfflineQueue(queue: OfflineOperation[]): OfflineOperation[] {
  if (!queue || queue.length <= 1) return queue;

  const pathMap = new Map<string, OfflineOperation[]>();
  const pathOrder: string[] = [];

  for (const op of queue) {
    if (!pathMap.has(op.path)) {
      pathMap.set(op.path, []);
      pathOrder.push(op.path);
    }
    pathMap.get(op.path)!.push(op);
  }

  const coalesced: OfflineOperation[] = [];

  for (const path of pathOrder) {
    const ops = pathMap.get(path)!;
    if (ops.length === 1) {
      coalesced.push(ops[0]);
      continue;
    }

    let finalOp: OfflineOperation | null = null;
    for (const op of ops) {
      if (!finalOp) {
        finalOp = { ...op };
        continue;
      }

      if (op.type === 'delete') {
        finalOp = {
          ...op,
          id: finalOp.id,
          timestamp: op.timestamp
        };
      } else if (op.type === 'set') {
        if (finalOp.type === 'set') {
          finalOp = {
            ...op,
            id: finalOp.id,
            options: op.options || finalOp.options,
            timestamp: op.timestamp
          };
        } else {
          finalOp = {
            ...op,
            id: finalOp.id,
            timestamp: op.timestamp
          };
        }
      }
    }

    if (finalOp) {
      coalesced.push(finalOp);
    }
  }

  return coalesced.sort((a, b) => a.timestamp - b.timestamp);
}

let syncDebounceTimer: any = null;
let isProcessingQueue = false;

export function triggerDebouncedSync() {
  if (typeof window === 'undefined') return;
  if (syncDebounceTimer) clearTimeout(syncDebounceTimer);
  syncDebounceTimer = setTimeout(() => {
    processOfflineQueue().catch(err => console.warn('[OfflineQueue] Auto-sync background flush error:', err));
  }, 1500);
}

export function queueOfflineOperation(operation: Omit<OfflineOperation, 'id' | 'timestamp'>) {
  const queue = getOfflineQueue();
  const id = Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
  queue.push({
    ...operation,
    id,
    timestamp: Date.now()
  });
  saveOfflineQueue(queue);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('offline-queue-updated', { detail: queue.length }));
  }
  triggerDebouncedSync();
}

export function getOfflineQueueLength() {
  return getOfflineQueue().length;
}

/**
 * Batches and executes all queued offline mutations using Firestore WriteBatches.
 * Process up to 100 items per atomic transaction chunk, falling back gracefully
 * to individual executions if a batch encounters non-network data errors.
 */
export async function processOfflineQueue() {
  if (isProcessingQueue) return;
  if (isQuotaExceeded) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  
  const rawQueue = getOfflineQueue();
  if (rawQueue.length === 0) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('offline-queue-updated', { detail: 0 }));
      window.dispatchEvent(new CustomEvent('syncing-ended'));
    }
    return;
  }

  isProcessingQueue = true;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('syncing-started'));
  }

  let remainingQueue = [...rawQueue];

  try {
    const queue = coalesceOfflineQueue(rawQueue);
    if (queue.length !== rawQueue.length) {
      saveOfflineQueue(queue);
      console.log(`[OfflineQueue] Coalesced queue from ${rawQueue.length} to ${queue.length} operations.`);
    }

    remainingQueue = [...queue];
    remainingQueue.sort((a, b) => a.timestamp - b.timestamp);

    const BATCH_SIZE = 100;

    while (remainingQueue.length > 0) {
      if (isQuotaExceeded) break;
      if (typeof navigator !== 'undefined' && !navigator.onLine) break;

      const chunk = remainingQueue.slice(0, BATCH_SIZE);

      try {
        const batch = writeBatch(db);
        let validCount = 0;

        for (const op of chunk) {
          const segments = op.path.split('/');
          if (segments.length % 2 !== 0 || segments.some(s => !s || s === 'undefined' || s === 'null')) {
            console.warn('[OfflineQueue] Invalid document path, skipping:', op.path);
            continue;
          }

          const docRef = doc(db, op.path);
          if (op.type === 'set') {
            if (op.options) {
              batch.set(docRef, safeClone(op.data), op.options);
            } else {
              batch.set(docRef, safeClone(op.data));
            }
          } else if (op.type === 'delete') {
            batch.delete(docRef);
          }
          validCount++;
        }

        if (validCount > 0) {
          await Promise.race([
            batch.commit(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Firestore batch commit timeout')), 8000))
          ]);
          console.log(`[OfflineQueue] Successfully committed batched write of ${validCount} operations.`);
        }

        const processedIds = new Set(chunk.map(c => c.id));
        const nextRemaining = remainingQueue.filter(op => !processedIds.has(op.id));
        remainingQueue.length = 0;
        remainingQueue.push(...nextRemaining);

        saveOfflineQueue(remainingQueue);

      } catch (batchError: any) {
        if (isFirestoreQuotaExhaustedError(batchError)) {
          setIsQuotaExceeded(true);
          console.warn('[OfflineQueue] Firestore quota exceeded while processing batch. Halting queue.');
          break;
        }

        const isNetworkError = batchError?.message?.includes('offline') || 
                               batchError?.code === 'unavailable' || 
                               batchError?.message?.includes('timeout') ||
                               (typeof navigator !== 'undefined' && !navigator.onLine);
        if (isNetworkError) {
          console.warn('[OfflineQueue] Network connection unavailable or timed out during batch commit. Pausing sync.');
          break;
        }

        console.warn('[OfflineQueue] Batch commit failed due to item error, falling back to individual processing:', batchError?.message || batchError);

        let chunkHasErrors = false;
        for (const op of chunk) {
          try {
            const segments = op.path.split('/');
            if (segments.length % 2 === 0 && !segments.some(s => !s || s === 'undefined' || s === 'null')) {
              const docRef = doc(db, op.path);
              if (op.type === 'set') {
                if (op.options) {
                  await Promise.race([
                    firestoreSetDoc(docRef, safeClone(op.data), op.options),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('SetDoc timeout')), 5000))
                  ]);
                } else {
                  await Promise.race([
                    firestoreSetDoc(docRef, safeClone(op.data)),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('SetDoc timeout')), 5000))
                  ]);
                }
              } else if (op.type === 'delete') {
                await Promise.race([
                  firestoreDeleteDoc(docRef),
                  new Promise((_, reject) => setTimeout(() => reject(new Error('DeleteDoc timeout')), 5000))
                ]);
              }
            } else {
              console.warn('[OfflineQueue] Purging malformed path:', op.path);
            }
            const index = remainingQueue.findIndex(item => item.id === op.id);
            if (index > -1) {
              remainingQueue.splice(index, 1);
            }
          } catch (individualError: any) {
            if (isFirestoreQuotaExhaustedError(individualError)) {
              setIsQuotaExceeded(true);
              chunkHasErrors = true;
              break;
            }

            if (individualError?.message?.includes('exceeds the maximum allowed size')) {
              console.warn('[OfflineQueue] Document too large, dropping from queue:', op.path);
              const index = remainingQueue.findIndex(item => item.id === op.id);
              if (index > -1) {
                remainingQueue.splice(index, 1);
              }
              continue;
            }

            const isPermDenied = individualError?.message?.includes('permission-denied') || 
                                 individualError?.message?.includes('missing or insufficient permissions');
            if (isPermDenied) {
              console.warn(`[OfflineQueue] Permission denied for ${op.path}, dropping unauthorizable operation:`, individualError?.message);
              const index = remainingQueue.findIndex(item => item.id === op.id);
              if (index > -1) {
                remainingQueue.splice(index, 1);
              }
              continue;
            }

            console.warn(`[OfflineQueue] Operation on ${op.path} failed:`, individualError?.message || individualError);
            chunkHasErrors = true;
            break;
          }
        }

        saveOfflineQueue(remainingQueue);
        if (chunkHasErrors) {
          break;
        }
      }
    }
  } catch (err) {
    console.error('[OfflineQueue] Unexpected queue processing error:', err);
  } finally {
    isProcessingQueue = false;
    saveOfflineQueue(remainingQueue);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('offline-queue-updated', { detail: remainingQueue.length }));
      window.dispatchEvent(new CustomEvent('syncing-ended'));
    }
  }
}

/**
 * Batches multiple write and delete operations into unified WriteBatches.
 * Immediately commits online or enqueues offline for seamless resilience.
 */
export async function batchWriteOperationsWithQueue(
  operations: Array<{ type: 'set' | 'delete'; path: string; data?: any; options?: any }>
): Promise<void> {
  if (!operations || operations.length === 0) return;

  if (isQuotaExceeded || (typeof navigator !== 'undefined' && !navigator.onLine)) {
    operations.forEach(op => queueOfflineOperation(op));
    return;
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('syncing-started'));
  }

  const BATCH_SIZE = 100;
  try {
    for (let i = 0; i < operations.length; i += BATCH_SIZE) {
      const chunk = operations.slice(i, i + BATCH_SIZE);
      try {
        const batch = writeBatch(db);
        for (const op of chunk) {
          const docRef = doc(db, op.path);
          if (op.type === 'set') {
            if (op.options) {
              batch.set(docRef, safeClone(op.data), op.options);
            } else {
              batch.set(docRef, safeClone(op.data));
            }
          } else if (op.type === 'delete') {
            batch.delete(docRef);
          }
        }
        await batch.commit();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('sync-success'));
        }
      } catch (error: any) {
        if (isFirestoreQuotaExhaustedError(error)) {
          setIsQuotaExceeded(true);
          operations.slice(i).forEach(op => queueOfflineOperation(op));
          return;
        }

        const isNetworkError = error?.message?.includes('offline') || 
                               error?.code === 'unavailable' || 
                               (typeof navigator !== 'undefined' && !navigator.onLine);
        if (isNetworkError) {
          operations.slice(i).forEach(op => queueOfflineOperation(op));
          return;
        }

        // Fallback for non-network errors in chunk
        for (const op of chunk) {
          const docRef = doc(db, op.path);
          try {
            if (op.type === 'set') {
              await setDocWithQueue(docRef, op.data, op.options);
            } else {
              await deleteDocWithQueue(docRef);
            }
          } catch (e) {
            console.warn(`[BatchWrite] Individual fallback write error for ${op.path}:`, e);
          }
        }
      }
    }
  } finally {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('syncing-ended'));
    }
  }
}

export async function batchSetDocsWithQueue(
  items: Array<{ docRef: any; data: any; options?: any }>
): Promise<void> {
  const operations = items.map(item => ({
    type: 'set' as const,
    path: item.docRef.path,
    data: item.data,
    options: item.options
  }));
  return batchWriteOperationsWithQueue(operations);
}

export async function batchDeleteDocsWithQueue(
  docRefs: any[]
): Promise<void> {
  const operations = docRefs.map(docRef => ({
    type: 'delete' as const,
    path: docRef.path
  }));
  return batchWriteOperationsWithQueue(operations);
}

export async function setDocWithQueue(docRef: any, data: any, options?: any) {
  if (isQuotaExceeded || (typeof navigator !== 'undefined' && !navigator.onLine)) {
    queueOfflineOperation({ type: 'set', path: docRef.path, data: safeClone(data), options });
    return;
  }
  
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('syncing-started'));
  }

  try {
    if (options) {
      await firestoreSetDoc(docRef, safeClone(data), options);
    } else {
      await firestoreSetDoc(docRef, safeClone(data));
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sync-success'));
    }
  } catch (error: any) {
    if (isFirestoreQuotaExhaustedError(error)) {
      setIsQuotaExceeded(true);
      queueOfflineOperation({ type: 'set', path: docRef.path, data: safeClone(data), options });
      return;
    }
    const isNetworkError = error?.message?.includes('offline') || error?.code === 'unavailable' || (typeof navigator !== 'undefined' && !navigator.onLine);
    if (isNetworkError) {
      queueOfflineOperation({ type: 'set', path: docRef.path, data: safeClone(data), options });
    } else {
      throw error;
    }
  } finally {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('syncing-ended'));
    }
  }
}

export async function deleteDocWithQueue(docRef: any) {
  if (isQuotaExceeded || (typeof navigator !== 'undefined' && !navigator.onLine)) {
    queueOfflineOperation({ type: 'delete', path: docRef.path });
    return;
  }
  
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('syncing-started'));
  }

  try {
    await firestoreDeleteDoc(docRef);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sync-success'));
    }
  } catch (error: any) {
    if (isFirestoreQuotaExhaustedError(error)) {
      setIsQuotaExceeded(true);
      queueOfflineOperation({ type: 'delete', path: docRef.path });
      return;
    }
    const isNetworkError = error?.message?.includes('offline') || error?.code === 'unavailable' || (typeof navigator !== 'undefined' && !navigator.onLine);
    if (isNetworkError) {
       queueOfflineOperation({ type: 'delete', path: docRef.path });
    } else {
      throw error;
    }
  } finally {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('syncing-ended'));
    }
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('[Network] System came online. Flushing batched offline queue...');
    if (isQuotaExceeded) {
      checkFirestoreConnection().then(ok => {
        if (ok) processOfflineQueue().catch(e => console.warn('[Network] Offline queue flush error:', e));
      });
    } else {
      processOfflineQueue().catch(e => console.warn('[Network] Offline queue flush error:', e));
    }
  });
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      if (isQuotaExceeded) {
        checkFirestoreConnection().catch(() => {});
      } else {
        processOfflineQueue().catch(e => console.warn('[Network] Visibility change sync error:', e));
      }
    }
  });
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  if (isFirestoreQuotaExhaustedError(error)) {
    console.warn('Firestore quota exceeded. Disabling background writes and falling back to local storage.');
    setIsQuotaExceeded(true);
    return; // Return gracefully to avoid unhandled exception in background tasks
  }

  const isPermissionError = errorMessage.toLowerCase().includes('missing or insufficient permissions') || errorMessage.toLowerCase().includes('permission-denied');
  
  if (isPermissionError) {
    const errInfo = {
      error: errorMessage,
      authInfo: {
        userId: auth.currentUser?.uid,
        email: auth.currentUser?.email,
        emailVerified: auth.currentUser?.emailVerified,
        isAnonymous: auth.currentUser?.isAnonymous,
        tenantId: auth.currentUser?.tenantId,
        providerInfo: auth.currentUser?.providerData.map(provider => ({
          providerId: provider.providerId,
          displayName: provider.displayName,
          email: provider.email,
          photoUrl: provider.photoURL
        })) || []
      },
      operationType,
      path
    };
    console.error('Firestore Error: ', safeStringify(errInfo));
    throw new Error(safeStringify(errInfo));
  } else {
    console.error('Firestore Error: ', errorMessage);
    throw new Error(errorMessage);
  }
}

async function testConnection() {
  try {
    // We use getDocFromServer to bypass cache and force a real network check
    await getDocFromServer(doc(db, 'test', 'connection'));
    console.log("Firestore connection successful.");
  } catch (error: any) {
    const isUnavailable = error?.code === 'unavailable' || error?.message?.includes('unavailable');
    if (isFirestoreQuotaExhaustedError(error)) {
      console.warn("Firestore Quota Exceeded during test connection.");
      return;
    }
    
    const errInfo = {
      error: error instanceof Error ? error.message : String(error),
      code: error?.code,
      suggestion: isUnavailable 
        ? "Firestore is currently unreachable. This usually means: 1. You have exceeded your daily free quota. 2. Your database is still provisioning. 3. A firewall is blocking firestore.googleapis.com."
        : "Standard Firestore error occurred.",
      authInfo: {
        userId: auth.currentUser?.uid,
        email: auth.currentUser?.email,
      },
      operationType: 'get',
      path: 'test/connection'
    };
    
    if (isUnavailable) {
      console.warn("Firestore Connectivity Warning:", errInfo.suggestion);
    } else {
      console.error("Firestore Test Error: ", safeStringify(errInfo));
    }
  }
}
// testConnection();
setLogLevel('silent');
